import { ActionIcon, Badge, Group, Table, Text, Tooltip } from "@mantine/core";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import type {
	LicenseFeature,
	LicenseType,
} from "../../store/licenseTypes/licenseTypesSlice";

interface LicenseTypeTableProps {
	licenseTypes: LicenseType[];
	features: LicenseFeature[];
	currentUserRole?: string;
	onEditLicenseType: (licenseType: LicenseType) => void;
	onDeleteLicenseType: (licenseType: LicenseType) => void;
}

function LicenseTypeTable({
	licenseTypes,
	features,
	currentUserRole,
	onEditLicenseType,
	onDeleteLicenseType,
}: LicenseTypeTableProps) {
	const canModifyLicenseTypes = currentUserRole === "admin";

	const toFeatureLabel = (key: string) =>
		features.find((feature) => feature.key === key)?.label || key;

	return (
		<Table.ScrollContainer
			minWidth={500}
			maxHeight={500}>
			<Table
				striped
				highlightOnHover>
				<Table.Thead>
					<Table.Tr>
						<Table.Th>Name</Table.Th>
						<Table.Th>Monthly Price</Table.Th>
						<Table.Th>Features</Table.Th>
						<Table.Th>Status</Table.Th>
						<Table.Th>Actions</Table.Th>
					</Table.Tr>
				</Table.Thead>
				<Table.Tbody>
					{licenseTypes.map((licenseType) => (
						<Table.Tr key={licenseType._id}>
							<Table.Td>
								<div>
									<Text fw={500}>{licenseType.name}</Text>
									{licenseType.description && (
										<Text
											size="xs"
											c="dimmed">
											{licenseType.description}
										</Text>
									)}
								</div>
							</Table.Td>
							<Table.Td>
								<Text fw={500}>₪{licenseType.monthlyPrice.toFixed(2)}</Text>
							</Table.Td>
							<Table.Td>
								{licenseType.features.length === 0 ? (
									<Text
										size="sm"
										c="dimmed">
										None
									</Text>
								) : (
									<Group gap="xs">
										{licenseType.features.map((feature) => (
											<Badge
												key={feature}
												variant="light"
												color="blue">
												{toFeatureLabel(feature)}
											</Badge>
										))}
									</Group>
								)}
							</Table.Td>
							<Table.Td>
								<Badge
									variant="light"
									color={licenseType.isActive ? "green" : "gray"}>
									{licenseType.isActive ? "Active" : "Withdrawn"}
								</Badge>
							</Table.Td>
							<Table.Td>
								<Group gap="xs">
									{canModifyLicenseTypes && (
										<Tooltip label="Edit license type">
											<ActionIcon
												variant="light"
												color="blue"
												onClick={() => onEditLicenseType(licenseType)}>
												<IconEdit size={16} />
											</ActionIcon>
										</Tooltip>
									)}
									{canModifyLicenseTypes && (
										<Tooltip label="Delete license type">
											<ActionIcon
												variant="light"
												color="red"
												onClick={() => onDeleteLicenseType(licenseType)}>
												<IconTrash size={16} />
											</ActionIcon>
										</Tooltip>
									)}
								</Group>
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}

export default LicenseTypeTable;
